/**
 * Common App Modal Component
 * 
 * A Common App styled popup modal for secure credential entry and data import.
 * Features:
 * - Common App branding and styling
 * - Security messaging and encryption indicators
 * - Progress notifications with toast integration
 * - Automatic import workflow
 */

import React, { useState } from 'react';
import { 
  IoClose, 
  IoShieldCheckmark, 
  IoEye,
  IoEyeOff,
  IoInformationCircle
} from 'react-icons/io5';
import { commonAppService } from '@/services/commonAppService';
import { ToastService } from '@/utils/toast';
import './CommonAppModal.css';

interface CommonAppModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete?: (importedData: any) => void;
}

const CommonAppModal: React.FC<CommonAppModalProps> = ({
  isOpen,
  onClose,
  onImportComplete
}) => {

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  const validateForm = () => {
    const newErrors: { email?: string; password?: string } = {};
    
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!email.includes('@') || !email.includes('.')) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!password.trim()) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImport = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsImporting(true);

    // Show importing toast
    const importingToastId = ToastService.showInfo('Importing data from Common App...', {
      duration: 0, // Don't auto-dismiss
      icon: '🔄'
    });

    try {
      // Validate credentials first
      const validation = await commonAppService.validateCredentials({ email, password });
      
      if (!validation.credentials_valid) {
        ToastService.dismiss(importingToastId);
        ToastService.showError('Invalid Common App credentials. Please check your email and password.');
        return;
      }

      // Import profile data
      const importResult = await commonAppService.importProfile({
        email,
        password,
        overwrite_existing: true
      });

      // Dismiss importing toast
      ToastService.dismiss(importingToastId);

      if (importResult.success) {
        // Show success toast
        ToastService.showSuccess(`Import complete! ${importResult.total_imported} fields imported successfully.`, {
          duration: 5000,
          icon: '✅'
        });
        
        // Call completion callback
        onImportComplete?.(importResult);
        
        // Close modal
        onClose();
        
        // Reset form
        setEmail('');
        setPassword('');
        setErrors({});
      } else {
        ToastService.showError(importResult.message || 'Import failed. Please try again.');
      }
    } catch (error: any) {
      ToastService.dismiss(importingToastId);
      ToastService.showError(error.message || 'Failed to import data. Please check your credentials and try again.');
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    if (!isImporting) {
      setEmail('');
      setPassword('');
      setErrors({});
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="commonapp-modal-overlay" onClick={handleClose}>
      <div 
        className="commonapp-modal-container dark:bg-gray-800" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="commonapp-modal-header">
          <div className="commonapp-modal-logo">
            <div className="commonapp-logo-circle">
              <img 
                src="https://res.cloudinary.com/dhleshvte/image/upload/v1765477806/Screenshot_2025-12-11_at_1.28.28_PM_pmqchn.png"
                alt="Common App Logo"
                className="commonapp-logo-image"
              />
            </div>
            <div>
              <h2 className="commonapp-modal-title dark:text-gray-100">
                Import from Common App
              </h2>
              <p className="commonapp-modal-subtitle dark:text-gray-400">
                Save time by importing your data from the Common App
              </p>
            </div>
          </div>
          
          {!isImporting && (
            <button 
              onClick={handleClose}
              className="commonapp-modal-close dark:text-gray-400 dark:hover:text-gray-200"
            >
              <IoClose size={24} />
            </button>
          )}
        </div>

        {/* Security Banner */}
        <div className="commonapp-security-banner dark:bg-green-900/20 dark:border-green-600">
          <div className="commonapp-security-content">
            <IoShieldCheckmark className="commonapp-security-icon text-green-500" />
            <div>
              <p className="commonapp-security-title dark:text-green-400">
                🔒 100% Secure & Encrypted
              </p>
              <p className="commonapp-security-text dark:text-green-300">
                Your credentials are encrypted and never stored. We only read your profile data.
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleImport} className="commonapp-modal-form">
          <div className="commonapp-form-group">
            <label className="commonapp-form-label dark:text-gray-200">
              Common App Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors({ ...errors, email: undefined });
              }}
              className={`commonapp-form-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${
                errors.email ? 'error' : ''
              }`}
              placeholder="your.email@example.com"
              disabled={isImporting}
              autoComplete="email"
            />
            {errors.email && (
              <p className="commonapp-form-error dark:text-red-400">
                {errors.email}
              </p>
            )}
          </div>

          <div className="commonapp-form-group">
            <label className="commonapp-form-label dark:text-gray-200">
              Common App Password
            </label>
            <div className="commonapp-password-container">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) setErrors({ ...errors, password: undefined });
                }}
                className={`commonapp-form-input dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${
                  errors.password ? 'error' : ''
                }`}
                placeholder="Your Common App password"
                disabled={isImporting}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="commonapp-password-toggle dark:text-gray-400 dark:hover:text-gray-200"
                disabled={isImporting}
              >
                {showPassword ? <IoEyeOff size={20} /> : <IoEye size={20} />}
              </button>
            </div>
            {errors.password && (
              <p className="commonapp-form-error dark:text-red-400">
                {errors.password}
              </p>
            )}
          </div>



          {/* Import Button */}
          <button
            type="submit"
            disabled={isImporting}
            className="commonapp-import-button"
          >
            {isImporting ? (
              <>
                <div className="commonapp-spinner" />
                Importing Data...
              </>
            ) : (
              <>
                <IoShieldCheckmark size={20} />
                Import My Data Securely
              </>
            )}
          </button>

          {/* Info Notice */}
          <div className="commonapp-info-notice dark:bg-blue-900/20 dark:border-blue-600">
            <IoInformationCircle className="commonapp-info-icon text-blue-500" />
            <div className="commonapp-info-content">
              <p className="commonapp-info-text dark:text-blue-300">
                We'll import your personal information, academic details, and test scores to help complete your Apply.AI profile.
              </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CommonAppModal;